import { z } from 'zod';
import { fulfillmentFileStatusSchema, fulfillmentLineResultSchema, marteletStateSchema, } from '../status/fulfillment.js';
import { flowModeSchema, runStatusSchema, runTriggerSchema } from '../status/run.js';
import { dateRangeQuerySchema, isoDateTimeSchema, objectIdSchema, paginationQuerySchema, sortQuerySchema, queryBooleanSchema, } from './common.js';
/** Compteurs d'un run de fulfillment (`03` §2, `fulfillmentRuns.stats`). */
export const fulfillmentRunStatsSchema = z.object({
    files: z.number().int().min(0),
    lines: z.number().int().min(0),
    fulfilled: z.number().int().min(0),
    alreadyFulfilled: z.number().int().min(0),
    stateUpdates: z.number().int().min(0),
    simulated: z.number().int().min(0),
    errors: z.number().int().min(0),
});
export const fulfillmentRunListItemSchema = z.object({
    id: objectIdSchema,
    startedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    trigger: runTriggerSchema,
    triggeredBy: objectIdSchema.nullable(),
    simulation: z.boolean(),
    status: runStatusSchema,
    stats: fulfillmentRunStatsSchema,
    error: z.string().nullable(),
});
export const fulfillmentRunListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    ...dateRangeQuerySchema.shape,
    status: z.array(runStatusSchema).optional(),
    simulation: queryBooleanSchema.optional(),
});
export const fulfillmentFileSchema = z.object({
    id: objectIdSchema,
    name: z.string(),
    sha256: z.string(),
    size: z.number().int().min(0),
    firstSeenAt: isoDateTimeSchema,
    runId: objectIdSchema.nullable(),
    status: fulfillmentFileStatusSchema,
    lineCount: z.number().int().min(0),
});
/** Résultat calculé en simulation (`03` §2, `fulfillmentLines.expected`). */
export const fulfillmentExpectedSchema = z.object({
    action: z.string(),
    fulfillmentOrderCount: z.number().int().min(0),
    company: z.string().nullable(),
});
export const fulfillmentLineSchema = z.object({
    id: objectIdSchema,
    fileId: objectIdSchema,
    fileName: z.string().nullable(),
    runId: objectIdSchema,
    lineNo: z.number().int().min(1),
    raw: z.string(),
    reference: z.string().nullable(),
    orderNumber: z.string().nullable(),
    state: z.string().nullable(),
    marteletState: marteletStateSchema.nullable(),
    trackingNumber: z.string().nullable(),
    trackingUrl: z.string().nullable(),
    store: z.string().nullable(),
    orderId: objectIdSchema.nullable(),
    simulated: z.boolean(),
    expected: fulfillmentExpectedSchema.nullable(),
    result: fulfillmentLineResultSchema,
    error: z.string().nullable(),
    retryCount: z.number().int().min(0),
    nextRetryRunAt: isoDateTimeSchema.nullable(),
    ignoredBy: objectIdSchema.nullable(),
});
export const fulfillmentRunDetailSchema = z.object({
    ...fulfillmentRunListItemSchema.shape,
    files: z.array(fulfillmentFileSchema),
    lines: z.array(fulfillmentLineSchema),
});
export const fulfillmentLineListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    runId: objectIdSchema.optional(),
    fileId: objectIdSchema.optional(),
    result: z.array(fulfillmentLineResultSchema).optional(),
    store: z.string().optional(),
    search: z.string().min(1).optional(),
});
export const fulfillmentRunStartBodySchema = z.object({
    /** `true` force un run simulé quel que soit le mode global (`01` §5.5). */
    simulation: z.boolean().default(false),
});
export const fulfillmentLineRetryBodySchema = z.object({
    lineIds: z.array(objectIdSchema).min(1),
});
export const fulfillmentLineIgnoreBodySchema = z.object({
    lineIds: z.array(objectIdSchema).min(1),
    reason: z.string().min(1, 'Motif obligatoire'),
});
/** Table « Transporteurs de retour » (`02` §6, `03` §2 `returnCarriers`). */
export const returnCarrierSchema = z.object({
    id: objectIdSchema,
    pattern: z.string().min(1),
    company: z.string().min(1),
    order: z.number().int().min(0),
});
export const returnCarrierInputSchema = returnCarrierSchema.omit({ id: true });
/**
 * Règle « commandes à ignorer » du Suivi Martelet (`01` §5.3.0) : une mise à
 * jour Martelet dont le numéro de commande correspond est ignorée sans
 * alerte — commandes créées directement chez Martelet (`UGC_…`, `TOASTY…`).
 */
export const FULFILLMENT_IGNORE_RULE_MATCHES = ['startsWith', 'contains', 'equals'];
export const fulfillmentIgnoreRuleMatchSchema = z.enum(FULFILLMENT_IGNORE_RULE_MATCHES);
export const FULFILLMENT_IGNORE_RULE_MATCH_LABELS = {
    startsWith: 'commence par',
    contains: 'contient',
    equals: 'est égal à',
};
export const fulfillmentIgnoreRuleInputSchema = z.object({
    match: fulfillmentIgnoreRuleMatchSchema,
    value: z.string().trim().min(2, 'Au moins deux caractères'),
    reason: z.string().trim().min(1, 'Motif obligatoire'),
});
export const fulfillmentIgnoreRuleSchema = fulfillmentIgnoreRuleInputSchema.extend({
    id: objectIdSchema,
    createdAt: isoDateTimeSchema,
    /** Mises à jour Martelet ignorées par cette règle, depuis sa création. */
    ignoredCount: z.number().int().min(0),
});
/** Création d'une règle : elle s'applique aussitôt aux mises à jour en erreur. */
export const fulfillmentIgnoreRuleCreateResultSchema = z.object({
    rule: fulfillmentIgnoreRuleSchema,
    /** Mises à jour en erreur ignorées à la création de la règle. */
    appliedToExisting: z.number().int().min(0),
});
export const fulfillmentSettingsSummarySchema = z.object({
    enabled: z.boolean().optional(),
    mode: flowModeSchema,
    schedule: z.string(),
    scheduleDescription: z.string(),
    defaultReturnCompany: z.string(),
    nextRuns: z.array(isoDateTimeSchema),
    runInProgress: z.boolean(),
});
/** Bandeau « simulation »/passage en `actif` (`02` §6). */
export const fulfillmentModeUpdateBodySchema = z.object({
    mode: flowModeSchema,
});
/** `01` §5.5 : « Lancer maintenant » / « Lancer en simulation » — résultat affiché en toast. */
export const fulfillmentRunResultSchema = z.object({
    skipped: z.boolean(),
    reason: z.enum(['maintenance', 'already_running', 'inactive']).nullable(),
    runId: objectIdSchema.nullable(),
    status: runStatusSchema.nullable(),
    simulation: z.boolean().nullable(),
    stats: fulfillmentRunStatsSchema.nullable(),
    error: z.string().nullable(),
});
/** Résultat d'une action (réessai/ignorer) sur une ligne, unitaire ou groupée (`02` §6). */
export const fulfillmentLineActionResultSchema = z.object({
    lineId: objectIdSchema,
    ok: z.boolean(),
    line: fulfillmentLineSchema.nullable(),
    error: z.string().nullable(),
});
export const fulfillmentLineBulkActionResultSchema = z.object({
    results: z.array(fulfillmentLineActionResultSchema),
});
/** Édition de la valeur par défaut de la table « Transporteurs de retour » (`02` §6). */
export const returnCarrierDefaultUpdateBodySchema = z.object({
    company: z.string().min(1),
});
export const returnCarriersResponseSchema = z.object({
    items: z.array(returnCarrierSchema),
    defaultReturnCompany: z.string(),
});
//# sourceMappingURL=fulfillment.js.map